import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import FormikField from '../../components/atoms/Form/FormikField';
import FormikInput from '../../components/atoms/Form/FormikInput';
import { SecondaryButton } from '../../components/atoms/Button/Button';
import resetPasswordFromEmail from '../../API/Auth/resetPasswordFromEmail';
import { ROUTES } from '../routerConfig';

const validationSchema = Yup.object({
  new_password1: Yup.string()
    .min(8, 'Password must be at least 8 characters.')
    .required('Required'),
  new_password2: Yup.string()
    .oneOf([Yup.ref('new_password1')], 'Passwords must match')
    .required('Required'),
});

function ResetPassword() {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const navigate = useNavigate();
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const handleSubmit = async (
    values: { new_password1: string; new_password2: string },
    helpers: FormikHelpers<{ new_password1: string; new_password2: string }>
  ) => {
    try {
      const response = await resetPasswordFromEmail(
        uid!,
        token!,
        values.new_password1,
        values.new_password2
      );

      if (response.success) {
        setSuccess('Password has been reset successfully.');
        setTimeout(() => navigate(ROUTES.LOGIN), 2000);
      } else {
        setError(response.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Something went wrong.');
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <div className="mt-8">
      <Formik
        initialValues={{ new_password1: '', new_password2: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form>
            <fieldset
              className="flex flex-col max-w-lg mx-auto p-4 border rounded"
              disabled={formik.isSubmitting}
            >
              {error && (
                <div className="text-red-600 text-sm mb-2">{error}</div>
              )}
              {success && (
                <div className="text-green-600 text-sm mb-2">{success}</div>
              )}
              <FormikField
                name="new_password1"
                label="New Password"
                placeholder="Enter your new password"
                component={FormikInput}
                type="password"
              />
              <FormikField
                name="new_password2"
                label="Confirm Password"
                placeholder="Confirm your new password"
                component={FormikInput}
                type="password"
              />
              <SecondaryButton type="submit" isLoading={formik.isSubmitting}>
                Reset Password
              </SecondaryButton>
            </fieldset>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ResetPassword;
